import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';
import './VigilRuleBrowser.css';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';

/* ── severity palette ──────────────────────────────────────────────────────── */
const SEV = {
  critical: { color: '#dc2626', label: 'Critical' },
  high:     { color: '#ea580c', label: 'High' },
  medium:   { color: '#ca8a04', label: 'Medium' },
  low:      { color: '#2563eb', label: 'Low' },
  info:     { color: '#6b7280', label: 'Info' },
};

const LANG_CLASS = {
  go: 'go', java: 'java', python: 'python', javascript: 'javascript',
  typescript: 'typescript', c: 'c', 'c++': 'cpp', cpp: 'cpp',
  dockerfile: 'dockerfile', yaml: 'yaml', hcl: 'hcl',
};

const FW_CLASS = {
  'EU-CRA': 'eucra', 'PCI-DSS': 'pcidss', 'OWASP': 'owasp',
  'ASVS': 'asvs', 'SOC2': 'soc2',
};

const FW_COLOR = {
  'EU-CRA': '#7c3aed', 'PCI-DSS': '#dc2626', 'OWASP': '#ea580c',
  'ASVS': '#0891b2', 'SOC2': '#059669',
};

const ALL_LANGS = ['Go','Java','Python','JavaScript','TypeScript','C','C++','Dockerfile','YAML','HCL'];
const ALL_FRAMEWORKS = ['EU-CRA','PCI-DSS','OWASP','ASVS','SOC2'];

/* ── animated counter ──────────────────────────────────────────────────────── */
function AnimatedCounter({ value, duration = 600 }) {
  const ref = useRef(null);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = value;
    if (start === end) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      if (ref.current) ref.current.textContent = Math.round(start + (end - start) * ease);
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = end;
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <span ref={ref}>{prev.current}</span>;
}

/* ── DSL syntax highlighter ────────────────────────────────────────────────── */
function highlightDSL(code) {
  if (!code) return null;
  const keywords = /\b(find|flow|from|to|where|as|not|and|or|in|match|taint|source|sink|sanitizer)\b/g;
  const concepts = /(@[a-zA-Z_][\w.]*)/g;
  const strings = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g;
  const comments = /(\/\/.*$)/gm;

  let parts = [];
  let last = 0;
  const combined = new RegExp(
    `(${comments.source})|(${strings.source})|(${concepts.source})|(${keywords.source})`,
    'gm'
  );
  let m;
  while ((m = combined.exec(code)) !== null) {
    if (m.index > last) parts.push(<span key={last}>{code.slice(last, m.index)}</span>);
    if (m[1]) parts.push(<span key={m.index} className="vrb-dsl-comment">{m[0]}</span>);
    else if (m[3]) parts.push(<span key={m.index} className="vrb-dsl-string">{m[0]}</span>);
    else if (m[5]) parts.push(<span key={m.index} className="vrb-dsl-concept">{m[0]}</span>);
    else if (m[6]) parts.push(<span key={m.index} className="vrb-dsl-keyword">{m[0]}</span>);
    else parts.push(<span key={m.index}>{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < code.length) parts.push(<span key={last}>{code.slice(last)}</span>);
  return parts;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  VigilRuleBrowser                                                          */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function VigilRuleBrowser() {
  const { t } = useTranslation();
  const { token } = useAuth();

  /* ── state ──────────────────────────────────────────────────────────────── */
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // filters
  const [search, setSearch] = useState('');
  const [sevFilter, setSevFilter] = useState(null);
  const [langFilter, setLangFilter] = useState('');
  const [fwFilter, setFwFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [page, setPage] = useState(1);

  // expanded pack
  const [expandedPack, setExpandedPack] = useState(null);
  const [expandedRules, setExpandedRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);

  // drawer
  const [drawerRule, setDrawerRule] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  /* ── fetch packs ────────────────────────────────────────────────────────── */
  const fetchPacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${config.BASE_URL}/vigil/rulepacks`, { headers });
      setPacks(res.data.packs || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  /* ── fetch pack rules ───────────────────────────────────────────────────── */
  const handleExpandPack = useCallback(async (packId) => {
    if (expandedPack === packId) { setExpandedPack(null); return; }
    setExpandedPack(packId);
    setLoadingRules(true);
    try {
      const res = await axios.get(`${config.BASE_URL}/vigil/rulepacks/${packId}`, { headers });
      setExpandedRules(res.data.rules || []);
    } catch {
      setExpandedRules([]);
    } finally {
      setLoadingRules(false);
    }
  }, [expandedPack, headers]);

  /* ── fetch single rule ──────────────────────────────────────────────────── */
  const openRuleDrawer = useCallback(async (ruleId) => {
    setDrawerLoading(true);
    setDrawerRule(null);
    try {
      const res = await axios.get(`${config.BASE_URL}/vigil/rules/${encodeURIComponent(ruleId)}`, { headers });
      setDrawerRule(res.data);
    } catch {
      setDrawerRule(null);
    } finally {
      setDrawerLoading(false);
    }
  }, [headers]);

  /* ── deep-link: open rule from URL hash (e.g. /vigil/rules#js-nosql-injection) */
  const location = useLocation();
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.length > 1) {
      const ruleId = decodeURIComponent(hash.slice(1));
      if (ruleId) openRuleDrawer(ruleId);
    }
  }, [location.hash, openRuleDrawer]);

  /* ── computed stats ─────────────────────────────────────────────────────── */
  const stats = useMemo(() => {
    const totalRules = packs.reduce((s, p) => s + (p.ruleCount || 0), 0);
    const allLangs = new Set();
    const allFw = new Set();
    packs.forEach(p => {
      (p.languages || []).forEach(l => allLangs.add(l));
      (p.frameworks || []).forEach(f => allFw.add(f));
    });
    return { totalRules, totalPacks: packs.length, languages: allLangs.size, frameworks: allFw.size };
  }, [packs]);

  /* ── all tags ───────────────────────────────────────────────────────────── */
  const allTags = useMemo(() => {
    const tagSet = new Set();
    packs.forEach(p => (p.tags || []).forEach(t => tagSet.add(t)));
    return [...tagSet].sort();
  }, [packs]);

  /* ── filtered packs ─────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return packs.filter(p => {
      if (q) {
        const haystack = [p.name, p.description, ...(p.tags || [])].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (sevFilter) {
        const sevs = p.severities || {};
        if (!sevs[sevFilter]) return false;
      }
      if (langFilter) {
        if (!(p.languages || []).some(l => l.toLowerCase() === langFilter.toLowerCase())) return false;
      }
      if (fwFilter) {
        if (!(p.frameworks || []).includes(fwFilter)) return false;
      }
      if (tagFilter) {
        if (!(p.tags || []).includes(tagFilter)) return false;
      }
      return true;
    });
  }, [packs, search, sevFilter, langFilter, fwFilter, tagFilter]);

  const pagedPacks = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return filtered.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filtered, page]);

  /* ── render helpers ─────────────────────────────────────────────────────── */
  const renderHeatmap = (sev) => {
    const total = Object.values(sev || {}).reduce((s, v) => s + v, 0);
    if (!total) return <div className="vrb-heatmap" />;
    return (
      <div className="vrb-heatmap">
        {['critical','high','medium','low','info'].map(k => {
          const pct = ((sev[k] || 0) / total) * 100;
          return pct > 0 ? (
            <div key={k} className={`vrb-heatmap-seg vrb-heatmap-${k}`} style={{ width: `${pct}%` }} />
          ) : null;
        })}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════ */
  /*  RENDER                                                                 */
  /* ═══════════════════════════════════════════════════════════════════════ */

  if (loading) {
    return (
      <div className="vrb">
        <div className="vrb-loading">
          <div className="vrb-spinner" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vrb">
        <div className="ce-blank">
          <div className="ce-blank-icon">⚠️</div>
          <div className="ce-blank-title">{error}</div>
          <button className="ce-btn ce-btn--primary" onClick={fetchPacks}>{t('common.retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="vrb">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <motion.div className="ce-page-header mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="ce-page-title">🛡️ {t('vigilRules.title')}</h2>
        <p style={{ color: 'var(--bs-secondary-color)' }}>{stats.totalRules} {t('vigilRules.rules')} across {stats.totalPacks} security packs</p>
      </motion.div>

      {/* ── Stats ───────────────────────────────────────────────────────────── */}
      <motion.div className="ce-kpi-row mb-4"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        {[
          { icon: '📋', value: stats.totalRules,  label: t('vigilRules.totalRules') },
          { icon: '📦', value: stats.totalPacks,  label: t('vigilRules.totalPacks') },
          { icon: '💻', value: stats.languages,   label: t('vigilRules.languagesCovered') },
          { icon: '🏛️', value: stats.frameworks,  label: t('vigilRules.frameworksMapped') },
        ].map((s, i) => (
          <div className="ce-kpi" key={i}>
            <div className="ce-kpi-value">{s.icon} <AnimatedCounter value={s.value} /></div>
            <div className="ce-kpi-label">{s.label}</div>
          </div>
        ))}
      </motion.div>

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <motion.div className="vrb-filters mb-3"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <div className="ce-filter-bar mb-2">
          <input
            className="ce-search-input"
            placeholder={t('vigilRules.searchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <select className="ce-filter-select" value={langFilter} onChange={e => { setLangFilter(e.target.value); setPage(1); }}>
            <option value="">{t('vigilRules.allLanguages')}</option>
            {ALL_LANGS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <span className="ce-filter-count">{filtered.length} {t('vigilRules.rules')}</span>
        </div>

        {/* severity chips */}
        <div className="vrb-filter-row">
          <div className="vrb-chips">
            <button
              className={`vrb-chip${!sevFilter ? ' active' : ''}`}
              onClick={() => setSevFilter(null)}
            >{t('vigilRules.allSeverities')}</button>
            {Object.entries(SEV).map(([k, v]) => (
              <button
                key={k}
                className={`vrb-chip${sevFilter === k ? ' active' : ''}`}
                style={{ '--chip-color': v.color }}
                onClick={() => setSevFilter(sevFilter === k ? null : k)}
              >
                <span className="vrb-chip-dot" style={{ background: v.color }} />
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* framework chips */}
        <div className="vrb-filter-row">
          <div className="vrb-chips">
            {ALL_FRAMEWORKS.map(fw => (
              <button
                key={fw}
                className={`vrb-fw-chip${fwFilter === fw ? ' active' : ''}`}
                style={{ '--fw-color': FW_COLOR[fw] }}
                onClick={() => setFwFilter(fwFilter === fw ? null : fw)}
              >{fw}</button>
            ))}
          </div>
        </div>

        {/* tag cloud */}
        {allTags.length > 0 && (
          <div className="vrb-filter-row">
            <div className="vrb-tag-cloud">
              {allTags.map(tag => (
                <button
                  key={tag}
                  className={`vrb-tag${tagFilter === tag ? ' active' : ''}`}
                  onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
                >{tag}</button>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* ── Pack Grid ───────────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="ce-blank">
          <div className="ce-blank-icon">🔍</div>
          <div className="ce-blank-title">{t('vigilRules.noRulesFound')}</div>
        </div>
      ) : (
        <>
        <div className="vrb-grid">
          <AnimatePresence>
            {pagedPacks.map((pack) => (
              <React.Fragment key={pack.id}>
                <motion.div
                  className={`vrb-pack-card${expandedPack === pack.id ? ' expanded' : ''}`}
                  onClick={() => handleExpandPack(pack.id)}
                  layout
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="vrb-pack-count">{pack.ruleCount} {t('vigilRules.rules')}</div>
                  <div className="vrb-pack-header">
                    <h3 className="vrb-pack-name">{pack.name}</h3>
                    <span className="vrb-pack-version">v{pack.version}</span>
                  </div>
                  <p className="vrb-pack-desc">{pack.description}</p>
                  {renderHeatmap(pack.severities)}
                  <div className="vrb-lang-badges">
                    {(pack.languages || []).map(l => (
                      <span key={l} className={`vrb-lang-badge vrb-lang-${LANG_CLASS[l.toLowerCase()] || 'c'}`}>
                        {l}
                      </span>
                    ))}
                  </div>
                  {(pack.tags || []).length > 0 && (
                    <div className="vrb-pack-tags">
                      {pack.tags.map(tag => <span key={tag} className="ce-tag">{tag}</span>)}
                    </div>
                  )}
                  {(pack.frameworks || []).length > 0 && (
                    <div className="vrb-fw-badges">
                      {pack.frameworks.map(fw => (
                        <span key={fw} className={`vrb-fw-badge vrb-fw-${FW_CLASS[fw] || 'owasp'}`}>{fw}</span>
                      ))}
                    </div>
                  )}
                </motion.div>

                {/* ── expanded rules panel ──────────────────────────────────── */}
                {expandedPack === pack.id && (
                  <motion.div
                    className="vrb-expanded-panel"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    {loadingRules ? (
                      <div className="vrb-loading" style={{ padding: '2rem' }}>
                        <div className="vrb-spinner" />
                      </div>
                    ) : (
                      <div className="ce-table-wrap">
                      <table className="ce-table">
                        <thead>
                          <tr>
                            <th>Severity</th>
                            <th>Rule ID</th>
                            <th>Confidence</th>
                            <th>Languages</th>
                            <th>CWE</th>
                            <th>OWASP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expandedRules.map(rule => (
                            <tr key={rule.id} onClick={() => openRuleDrawer(rule.id)} style={{ cursor: 'pointer' }}>
                              <td>
                                <span className={`ce-sev ce-sev--${rule.severity}`}>{rule.severity}</span>
                              </td>
                              <td><span className="vrb-rule-id">{rule.id}</span></td>
                              <td>
                                <span className={`vrb-conf-badge vrb-conf-${rule.confidence || 'medium'}`}>
                                  {rule.confidence || 'medium'}
                                </span>
                              </td>
                              <td>
                                <div className="vrb-lang-badges">
                                  {(rule.languages || []).map(l => (
                                    <span key={l} className={`vrb-lang-badge vrb-lang-${LANG_CLASS[l.toLowerCase()] || 'c'}`}>
                                      {l}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="vrb-drawer-badges-row">
                                  {(rule.cwe || []).map(c => (
                                    <span key={c} className="vrb-cwe-badge">CWE-{c}</span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="vrb-drawer-badges-row">
                                  {(rule.owasp || []).map(o => (
                                    <span key={o} className="vrb-owasp-badge">{o}</span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </AnimatePresence>
        </div>
        {filtered.length > DEFAULT_PAGE_SIZE && (
          <div style={{ marginTop: '1.5rem' }}>
            <Pagination
              total={filtered.length}
              page={page}
              onPageChange={setPage}
            />
          </div>
        )}
        </>
      )}

      {/* ── Rule Detail Drawer ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {(drawerRule || drawerLoading) && (
          <>
            <motion.div
              className="vrb-drawer-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setDrawerRule(null); setDrawerLoading(false); }}
            />
            <motion.div
              className="vrb-drawer"
              initial={{ x: 440 }}
              animate={{ x: 0 }}
              exit={{ x: 440 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            >
              {drawerLoading ? (
                <div className="vrb-loading" style={{ flex: 1 }}>
                  <div className="vrb-spinner" />
                </div>
              ) : drawerRule && (
                <>
                  <div className="vrb-drawer-header">
                    <div>
                      <div className="vrb-drawer-title">{drawerRule.id}</div>
                      <div className="vrb-drawer-badges">
                        <span className={`ce-sev ce-sev--${drawerRule.severity}`}>{drawerRule.severity}</span>
                        <span className={`vrb-conf-badge vrb-conf-${drawerRule.confidence || 'medium'}`}>
                          {drawerRule.confidence || 'medium'}
                        </span>
                        <Link
                          to={`/vigil/rules/${encodeURIComponent(drawerRule.id)}`}
                          style={{ fontSize: '0.78rem', color: '#6366f1', fontWeight: 600, textDecoration: 'none', marginLeft: 6 }}
                          title="View full documentation"
                          onClick={() => setDrawerRule(null)}
                        >📄 Full docs →</Link>
                      </div>
                    </div>
                    <button
                      className="vrb-drawer-close"
                      onClick={() => setDrawerRule(null)}
                      aria-label={t('common.close')}
                    >✕</button>
                  </div>
                  <div className="vrb-drawer-body">
                    {/* Classification */}
                    <div className="vrb-drawer-section">
                      <h4>{t('vigilRules.classification')}</h4>
                      <div className="vrb-drawer-badges-row" style={{ marginBottom: '0.5rem' }}>
                        {(drawerRule.cwe || []).map(c => (
                          <a
                            key={c}
                            href={`https://cwe.mitre.org/data/definitions/${c}.html`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vrb-cwe-badge"
                          >CWE-{c}</a>
                        ))}
                      </div>
                      <div className="vrb-drawer-badges-row" style={{ marginBottom: '0.5rem' }}>
                        {(drawerRule.owasp || []).map(o => (
                          <span key={o} className="vrb-owasp-badge">{o}</span>
                        ))}
                      </div>
                      {(drawerRule.frameworks || []).length > 0 && (
                        <div className="vrb-fw-badges">
                          {drawerRule.frameworks.map(fw => (
                            <span key={fw} className={`vrb-fw-badge vrb-fw-${FW_CLASS[fw] || 'owasp'}`}>{fw}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Scope */}
                    <div className="vrb-drawer-section">
                      <h4>{t('vigilRules.scope')}</h4>
                      <div className="vrb-lang-badges" style={{ marginBottom: '0.5rem' }}>
                        {(drawerRule.languages || []).map(l => (
                          <span key={l} className={`vrb-lang-badge vrb-lang-${LANG_CLASS[l.toLowerCase()] || 'c'}`}>
                            {l}
                          </span>
                        ))}
                      </div>
                      {(drawerRule.excludedPaths || []).length > 0 && (
                        <>
                          <div style={{ fontSize: '0.78rem', color: 'var(--bs-secondary-color)', marginBottom: '0.3rem' }}>
                            {t('vigilRules.excludedPaths')}
                          </div>
                          <div className="vrb-excluded-paths">
                            {drawerRule.excludedPaths.map((p, i) => (
                              <span key={i} className="vrb-excluded-path">{p}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Detection Query */}
                    {drawerRule.query && (
                      <div className="vrb-drawer-section">
                        <h4>{t('vigilRules.detectionQuery')}</h4>
                        <pre className="vrb-dsl-block">{highlightDSL(drawerRule.query)}</pre>
                      </div>
                    )}

                    {/* Finding Message */}
                    {drawerRule.message && (
                      <div className="vrb-drawer-section">
                        <h4>{t('vigilRules.findingMessage')}</h4>
                        <div className="vrb-message-box">{drawerRule.message}</div>
                      </div>
                    )}

                    {/* Remediation */}
                    {drawerRule.fix && (
                      <div className="vrb-drawer-section">
                        <h4>{t('vigilRules.remediation')}</h4>
                        <div className="vrb-fix-box">{drawerRule.fix}</div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
